import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications } from "@/hooks/useNotifications";
import { useMessages } from "@/hooks/useMessages";
import { useTranslation } from "@/contexts/TranslationContext";
import { Bell, MessageCircle, User, Home, Map, Heart, Users, Camera, Search, Mail, LogOut, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
export function AppSidebar() {
  const {
    open
  } = useSidebar();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    user,
    profile,
    signOut
  } = useAuth();
  const {
    unreadCount: notificationsCount
  } = useNotifications();
  const {
    unreadCount: messagesCount
  } = useMessages();
  
  // Dynamic navigation items with translations
  const mainNavItems = [{
    name: t('nav.home'),
    href: "/app",
    icon: Home
  }, {
    name: t('nav.feed'),
    href: "/app/feed",
    icon: Camera
  }, {
    name: t('nav.map'),
    href: "/app/map",
    icon: Map
  }, {
    name: t('nav.messages'),
    href: "/app/messages",
    icon: MessageCircle
  }];
  
  const sidebarItems = [{
    name: t('menu.explore'),
    href: "/app/explore",
    icon: Search
  }, {
    name: t('menu.myPets'),
    href: "/app/pets",
    icon: Heart
  }, {
    name: t('menu.adoptions'),
    href: "/app/adoption",
    icon: Users
  }, {
    name: t('menu.receivedRequests'),
    href: "/app/adoption-requests",
    icon: Mail
  }, {
    name: t('menu.sentRequests'),
    href: "/app/my-adoption-requests",
    icon: Heart
  }, {
    name: t('common.events'),
    href: "/app/events",
    icon: Users
  }, {
    name: t('shop.title'),
    href: "/app/shop",
    icon: Store
  }, {
    name: t('common.notifications'),
    href: "/app/notifications",
    icon: Bell
  }];
  const isActive = (path: string) => location.pathname === path;
  return <Sidebar className="border-r border-border/30 glass-strong backdrop-blur-xl">
      {/* Header con Logo */}
      <SidebarHeader className="p-6 gradient-mesh border-b border-border/30">
        <Link to="/app" className="flex items-center space-x-3 transition-elastic hover:scale-105">
          <div className="w-14 h-14 rounded-2xl glass-strong border border-primary/30 flex items-center justify-center transition-elastic hover:scale-110 hover:glow-primary animate-float">
            <img src="/lovable-uploads/5e002d38-22aa-49af-85ae-34109191ea66.png" alt="AstroPet Logo" className="w-12 h-12" />
          </div>
          {open && <span className="text-xl font-bold gradient-text font-space tracking-wide">
              AstroPet
            </span>}
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-6 py-4 gradient-cosmic">
        {/* Navigazione Principale */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2 mb-2">
            {open ? t('menu.navigation') : "Nav"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map(item => <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton asChild>
                    <Link to={item.href} className={`group flex items-center px-4 py-4 text-sm rounded-2xl transition-elastic mb-2 ${isActive(item.href) ? "gradient-primary text-white shadow-primary border border-primary/50 scale-105" : "text-muted-foreground hover:text-foreground glass hover:glass-strong hover:border-primary/30 border border-border/20 hover:scale-[1.02]"}`}>
                      <div className={`p-2.5 rounded-xl mr-4 transition-elastic ${isActive(item.href) ? "bg-white/25 shadow-lg" : "glass group-hover:glass-strong group-hover:border-primary/20 border border-border/10"}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      {open && <>
                          <span className="font-medium flex-1">{item.name}</span>
                          {/* Badge per messaggi */}
                          {item.name === "Chat" && messagesCount > 0 && <Badge className="w-5 h-5 p-0 text-xs bg-secondary border-2 border-background animate-pulse">
                              {messagesCount > 99 ? '99+' : messagesCount}
                            </Badge>}
                        </>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Altre Sezioni */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2 mb-2">
            {open ? "Servizi" : "Ser"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarItems.map(item => <SidebarMenuItem key={item.name}>
                  <SidebarMenuButton asChild>
                    <Link to={item.href} className={`group flex items-center px-3 py-3 text-sm rounded-xl transition-all duration-300 ${isActive(item.href) ? "gradient-primary text-white glow-primary shadow-lg" : "text-muted-foreground hover:text-foreground glass hover:bg-white/5 hover:border-primary/20 border border-transparent"}`}>
                      <div className={`p-2 rounded-lg mr-3 transition-all ${isActive(item.href) ? "bg-white/20" : "bg-white/5 group-hover:bg-white/10"}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      {open && <>
                          <span className="font-medium flex-1">{item.name}</span>
                          {/* Badge per notifiche */}
                          {item.name === "Notifiche" && notificationsCount > 0 && <Badge className="w-5 h-5 p-0 text-xs bg-destructive border-2 border-background animate-pulse">
                              {notificationsCount > 99 ? '99+' : notificationsCount}
                            </Badge>}
                        </>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer con Profilo */}
      <SidebarFooter className="p-4 bg-slate-900">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link to="/app/profile" className={`group flex items-center px-3 py-3 text-sm rounded-xl transition-all duration-300 ${isActive("/app/profile") ? "gradient-primary text-white glow-primary shadow-lg" : "text-muted-foreground hover:text-foreground glass hover:bg-white/5 hover:border-primary/20 border border-transparent"}`}>
                <Avatar className="w-8 h-8 ring-2 ring-primary/60 hover:ring-primary transition-all mr-3">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white font-bold text-sm mx-0 py-0 px-0">
                    {profile?.display_name?.charAt(0).toUpperCase() || profile?.username?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                {open && <div className="flex-1 min-w-0">
                    <div className="font-medium truncate mx-px my-0 px-0 py-0">Profilo</div>
                    <div className="text-xs text-muted-foreground truncate mx-px py-0 my-0 px-0">
                      {profile?.display_name || profile?.username || 'Utente'}
                    </div>
                  </div>}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          
          {open && <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Button variant="ghost" onClick={() => signOut()} className="w-full justify-start px-3 py-3 text-sm rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors">
                  <LogOut className="w-5 h-5 mr-3" />
                  <span className="font-medium">Esci</span>
                </Button>
              </SidebarMenuButton>
            </SidebarMenuItem>}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>;
}